/**
 * Typed fetch wrapper — T20.
 *
 * Behavior:
 * 1. Adds `Authorization: Bearer <accessToken>` unless skipAuth=true.
 * 2. Adds `Content-Type: application/json` for POST/PUT/PATCH.
 * 3. Sets `credentials: 'include'` (required for the __Host-ms_refresh cookie).
 * 4. On 401: attempts POST /api/auth/refresh once, then retries the original
 *    request. If the retry also returns 401, calls authStore.forceLogout() and
 *    throws ApiError.
 * 5. On non-2xx: throws ApiError(code, message, fieldErrors?, status).
 * 6. In DEV mode, optionally validates the response body against a Zod schema
 *    (warn-only — never blocks the caller).
 * 7. Returns the full parsed JSON body (callers receive the raw { ok, ...data }
 *    envelope; endpoint wrappers can unwrap further if needed).
 *
 * SSE streaming:
 * openSSE(path, handlers) — uses fetch + ReadableStream to consume
 * text/event-stream without requiring custom EventSource headers.
 */

// Vite-injected env types now live in src/vite-env.d.ts — keep this import
// list at the top so biome's organizeImports doesn't shuffle reference
// directives around again.
import { ApiError } from './errors';
import { parseEventsFromReadableStream } from './sse-parser';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Minimal interface that duck-types the Zod parse result — avoids a direct
 * `zod` import in the web package (zod is a dep of @malaberg/shared only).
 */
interface ZodLike {
  safeParse(data: unknown): { success: boolean; error?: { issues: unknown[] } };
}

export interface FetchOptions extends Omit<RequestInit, 'method'> {
  /** When true, the Authorization header is omitted (used for login/refresh). */
  skipAuth?: boolean;
  /** Optional Zod schema for DEV-mode response validation (warn-only). */
  schema?: ZodLike;
  signal?: AbortSignal;
}

export interface AuthStore {
  accessToken: string | null;
  /** Called on consecutive 401s — clears state and redirects to /login. */
  forceLogout: () => void;
  /**
   * Called after a silent token refresh so the retry carries the new token.
   * If omitted, the retry will still proceed (server-side the cookie is rotated).
   */
  setAccessToken?: (token: string, expiresAt: Date) => void;
}

export interface SSEHandlers {
  onEvent: (event: { event: string; data: string }) => void;
  onError: (err?: Error) => void;
  signal?: AbortSignal;
}

export interface SSEHandle {
  close(): void;
}

// ─── ApiClient class ──────────────────────────────────────────────────────────

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly getAuthStore: () => AuthStore
  ) {}

  /**
   * Core request method. Returns the full parsed JSON body.
   * Throws ApiError on any non-2xx response.
   */
  async request<T = unknown>(method: string, path: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth, schema, signal, ...init } = options;
    return this._doRequest<T>(method, path, { skipAuth, schema, signal, ...init }, false);
  }

  /**
   * Consume a server-sent event stream using fetch + ReadableStream.
   * This avoids the EventSource limitation of not supporting custom headers.
   */
  openSSE(path: string, handlers: SSEHandlers): SSEHandle {
    const abortController = new AbortController();
    const signal = handlers.signal
      ? AbortSignal.any([handlers.signal, abortController.signal])
      : abortController.signal;

    const store = this.getAuthStore();
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    if (store.accessToken) {
      headers.Authorization = `Bearer ${store.accessToken}`;
    }

    fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      signal,
    })
      .then((res) => {
        if (!res.ok || !res.body) {
          handlers.onError(new Error(`SSE connection failed: ${res.status}`));
          return;
        }
        return parseEventsFromReadableStream(res.body, handlers.onEvent, handlers.onError, signal);
      })
      .catch((err) => {
        // AbortError is expected when close() is called — don't surface as error
        if (err instanceof Error && err.name !== 'AbortError') {
          handlers.onError(err);
        }
      });

    return {
      close() {
        abortController.abort();
      },
    };
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  /**
   * Internal implementation that supports one automatic refresh+retry cycle.
   *
   * @param isRetry - true when this is the second attempt after a token refresh.
   *                  We never retry more than once to prevent infinite loops.
   */
  private async _doRequest<T>(
    method: string,
    path: string,
    options: FetchOptions,
    isRetry: boolean
  ): Promise<T> {
    const { skipAuth, schema, signal, ...init } = options;
    const store = this.getAuthStore();

    // Build headers
    const headers: Record<string, string> = {};

    if (!skipAuth && store.accessToken) {
      headers.Authorization = `Bearer ${store.accessToken}`;
    }

    const upperMethod = method.toUpperCase();
    if (['POST', 'PUT', 'PATCH'].includes(upperMethod) && init.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      method: upperMethod,
      headers: { ...headers, ...(init.headers as Record<string, string> | undefined) },
      credentials: 'include',
      signal,
    });

    // ── 401 handling ────────────────────────────────────────────────────────
    // A 401 can mean two distinct things:
    //   (a) the access token is invalid/expired (auth-layer 401) — try refresh
    //       once, retry; on a second 401, log out.
    //   (b) a domain-level 401 from the endpoint itself (e.g. wrong current
    //       password on /api/auth/change-password, bad creds on /login). The
    //       session is still valid — we must NOT force-logout.
    //
    // The two are distinguished by the response body's `code`. Auth-layer 401s
    // carry codes like `token_invalid`, `token_expired`, or `session_expired`.
    // Domain 401s carry their own codes (`wrong_password`, `invalid_credentials`,
    // `no_users`, etc.).
    if (response.status === 401) {
      // Peek at the body — clone so we can still surface the proper ApiError
      // if it's a domain 401.
      const peek = (await response
        .clone()
        .json()
        .catch(() => null)) as { code?: string; error?: string } | null;
      const code = peek?.code ?? '';
      const isAuthLayer =
        code === 'token_invalid' ||
        code === 'token_expired' ||
        code === 'session_expired' ||
        code === ''; // empty/missing code → safest to treat as auth-layer

      if (isAuthLayer) {
        if (isRetry) {
          store.forceLogout();
          throw new ApiError(
            'session_expired',
            'Session expired. Please log in again.',
            undefined,
            401
          );
        }
        await this._refreshToken();
        return this._doRequest<T>(method, path, options, true);
      }

      // Domain 401 — surface as-is. No retry, no logout.
      throw new ApiError(code || 'unauthorized', peek?.error ?? 'Unauthorized', undefined, 401);
    }

    // ── Non-2xx error handling ───────────────────────────────────────────────
    if (!response.ok) {
      await this._throwApiError(response);
    }

    // ── Success — parse body ─────────────────────────────────────────────────
    const text = await response.text();
    let data: T;
    try {
      data = JSON.parse(text) as T;
    } catch {
      // Extremely rare: 2xx but non-JSON body
      return text as unknown as T;
    }

    // DEV-mode schema validation (warn-only, never blocks)
    if (import.meta.env?.DEV && schema) {
      const result = schema.safeParse(data);
      if (!result.success) {
        console.warn('[ApiClient] Response schema mismatch:', path, result.error?.issues);
      }
    }

    return data;
  }

  /** Attempt to refresh the access token. Updates the store if successful. */
  private async _refreshToken(): Promise<void> {
    // NOTE: No Content-Type header — the refresh endpoint expects no request body.
    // Sending Content-Type: application/json with an empty body causes Fastify to
    // reject with "Body cannot be empty when content-type is set to 'application/json'".
    const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      return; // Let the retry handle the resulting 401
    }

    try {
      const body = (await response.json()) as {
        ok: boolean;
        accessToken?: string;
        accessTokenExpiresAt?: string;
      };

      if (body.ok && body.accessToken) {
        const store = this.getAuthStore();
        const expiresAt = body.accessTokenExpiresAt
          ? new Date(body.accessTokenExpiresAt)
          : new Date(Date.now() + 15 * 60 * 1000);

        if (store.setAccessToken) {
          // Notify the Zustand store so React re-renders pick up the new token
          store.setAccessToken(body.accessToken, expiresAt);
        }
        // Also update the module-level reference so _doRequest retry reads new token.
        // _authStore is reassigned via wireAuthStore; since getAuthStore returns _authStore
        // by reference we directly update the mutable field here as a fast path.
        (store as { accessToken: string | null }).accessToken = body.accessToken;
      }
    } catch {
      // Ignore parse errors — let the retry handle the outcome
    }
  }

  /** Parse non-2xx response and throw ApiError. */
  private async _throwApiError(response: Response): Promise<never> {
    const contentType = response.headers.get('Content-Type') ?? '';
    let body: {
      error?: string;
      code?: string;
      fieldErrors?: Record<string, string>;
    } = {};

    if (contentType.includes('application/json')) {
      try {
        body = (await response.json()) as typeof body;
      } catch {
        // Swallow parse errors — use defaults below
      }
    } else {
      // Drain non-JSON body to avoid resource leak
      await response.text().catch(() => undefined);
    }

    throw new ApiError(
      body.code ?? 'unknown_error',
      body.error ?? `HTTP ${response.status}`,
      body.fieldErrors,
      response.status
    );
  }
}

// ─── Singleton instance ───────────────────────────────────────────────────────
//
// The singleton is wired to the auth-store at module initialization time.
// This avoids circular imports: auth-store imports authApi endpoints which
// import apiClient — if apiClient imported auth-store directly we'd have a cycle.
// Instead, auth-store passes itself to wireAuthStore() after creation.

let _authStore: AuthStore = {
  accessToken: null,
  forceLogout: () => {
    // no-op until wired
  },
};

/**
 * Called once by auth-store.ts after the Zustand store is created.
 * Binds the live store reference so the client always reads the current token.
 */
export function wireAuthStore(store: AuthStore): void {
  _authStore = store;
}

export const apiClient = new ApiClient(
  // In browser: empty string → relative URLs (Vite dev proxy + nginx in prod)
  // In Node tests: the test sets up msw with 'http://localhost' so we need the full base.
  typeof window !== 'undefined' ? '' : 'http://localhost',
  () => _authStore
);
